import { useState, useEffect } from 'react';
import { apiClient } from '@/lib/api';

interface Lesson {
  id: number;
  tracker_id: number;
  title: string;
  order: number;
  is_completed: boolean;
  completed_at: string | null;
  started_at: string;
  time_spent: number;
}

interface Section {
  id: number;
  title: string;
  order: number;
  lessons: Lesson[];
}

interface CourseProgress {
  enrollment_id: number;
  course_title: string;
  overall_progress: number;
  enrolled_at: string;
  sections: Section[];
}

interface CourseProgressProps {
  enrollmentId: number;
}

export default function CourseProgress({ enrollmentId }: CourseProgressProps) {
  const [progress, setProgress] = useState<CourseProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<number | null>(null);

  useEffect(() => {
    loadCourseProgress();
  }, [enrollmentId]);

  const loadCourseProgress = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getEnrollmentProgress(enrollmentId);
      
      if (response.success && response.data) {
        setProgress(response.data);
      } else {
        setError(response.error || 'Failed to load course progress');
      }
    } catch (err) {
      setError('Failed to load course progress');
    } finally {
      setLoading(false);
    }
  };

  const toggleLessonCompletion = async (lessonId: number, trackerId: number, isCompleted: boolean) => {
    try {
      setUpdating(lessonId);

      const response = isCompleted
        ? await apiClient.completeLesson(trackerId)
        : await apiClient.incompleteLesson(trackerId);

      if (response.success) {
        await loadCourseProgress();
      } else {
        setError(response.error || 'Failed to update lesson status');
      }
    } catch (err) {
      setError('Failed to update lesson status');
    } finally {
      setUpdating(null);
    }
  };

  const formatTimeSpent = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-800">{error}</p>
        <button 
          onClick={loadCourseProgress}
          className="mt-2 text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!progress) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">No progress data available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Course Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{progress.course_title}</h2>
            <p className="text-gray-500 mt-1">Enrolled on {formatDate(progress.enrolled_at)}</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-blue-600">{progress.overall_progress}%</div>
            <div className="text-sm text-gray-500">Complete</div>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Course Progress</span>
            <span className="text-sm font-medium text-gray-700">{progress.overall_progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${progress.overall_progress}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Sections and Lessons */}
      <div className="space-y-4">
        {progress.sections.map((section) => (
          <div key={section.id} className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Section {section.order}: {section.title}
              </h3>
            </div>
            
            <div className="divide-y divide-gray-200">
              {section.lessons.map((lesson) => (
                <div key={lesson.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => toggleLessonCompletion(lesson.id, lesson.tracker_id, !lesson.is_completed)}
                        disabled={updating === lesson.id}
                        className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                          lesson.is_completed
                            ? 'bg-green-500 border-green-500 text-white'
                            : 'border-gray-300 hover:border-green-500'
                        }`}
                      >
                        {lesson.is_completed && (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                      
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900">
                          Lesson {lesson.order}: {lesson.title}
                        </h4>
                        <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                          <span>Started: {formatDate(lesson.started_at)}</span>
                          {lesson.time_spent > 0 && (
                            <span>Time: {formatTimeSpent(lesson.time_spent)}</span>
                          )}
                          {lesson.is_completed && lesson.completed_at && (
                            <span>Completed: {formatDate(lesson.completed_at)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {lesson.is_completed && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Completed
                        </span>
                      )}
                      {updating === lesson.id && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
} 